import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

/**
 * Chocolate Frog Card — the collectible.
 *
 * Opens from its own trigger or from the `alohomora` spell. As in the books the
 * frog gets one good leap before you can read the card, and the portrait on the
 * front doesn't hold still.
 */

const ACHIEVEMENTS = [
  { label: "LeetCode Guardian", value: "2244", note: "Top 0.8% worldwide" },
  { label: "Best Contest Rank", value: "264th", note: "40K+ duellists" },
  { label: "Problems Solved", value: "1700+", note: "across all platforms" },
  { label: "GATE 2021", value: "AIR 2547", note: "Top 3% of 100K+" },
  { label: "DU Entrance", value: "AIR 13", note: "20K+ candidates" },
  { label: "BOSS'20", value: "Rank 6", note: "Bountiful Open Source Summer" },
];

/* Cinzel small-caps would render "264th" as "264TH" — set the suffix apart. */
function withOrdinals(text: string): React.ReactNode {
  const parts = text.split(/(\d+(?:st|nd|rd|th))/gi);
  return parts.map((part, i) => {
    const m = /^(\d+)(st|nd|rd|th)$/i.exec(part);
    if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
    return <React.Fragment key={i}>{m[1]}<Ord>{m[2].toLowerCase()}</Ord></React.Fragment>;
  });
}

const FrogCard: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [frogGone, setFrogGone] = useState(false);
  const location = useLocation();

  const show = useCallback(() => {
    setOpen(true);
    setFlipped(false);
    setFrogGone(false);
  }, []);

  // `alohomora` breaks the seal
  useEffect(() => {
    const onCast = () => show();
    window.addEventListener("cast:alohomora", onCast);
    return () => window.removeEventListener("cast:alohomora", onCast);
  }, [show]);

  useEffect(() => {
    if (!open || frogGone) return;
    const id = window.setTimeout(() => setFrogGone(true), 1500);
    return () => window.clearTimeout(id);
  }, [open, frogGone]);

  // A spell like `accio` can navigate out from under the card
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <Trigger onClick={show} aria-label="Open the Chocolate Frog Card">
        <TriggerGlyph>&#128046;</TriggerGlyph>
        <TriggerText>The Card</TriggerText>
      </Trigger>

      {open && (
        <Backdrop onClick={() => setOpen(false)}>
          {!frogGone && <Frog aria-hidden="true">&#128056;</Frog>}

          <Stage onClick={(e) => e.stopPropagation()}>
            <CardShell>
            <Card $flipped={flipped} onClick={() => setFlipped((f) => !f)}>
              {/* ---------- front ---------- */}
              <Face>
                <Filigree />
                <PentagonFrame>
                  <Portrait>
                    <PortraitGlow />
                    <Silhouette>
                      <svg viewBox="0 0 100 120" aria-hidden="true">
                        {/* pointed hat */}
                        <path d="M50 8 L30 52 L70 52 Z" fill="#2a1f3d" />
                        <ellipse cx="50" cy="53" rx="26" ry="5" fill="#221933" />
                        {/* face + shoulders */}
                        <circle cx="50" cy="68" r="14" fill="#c98d63" />
                        <path d="M22 120 Q26 88 50 86 Q74 88 78 120 Z" fill="#7a1f22" />
                        <path d="M50 86 L50 120" stroke="#d3a625" strokeWidth="2" />
                        {/* spectacles */}
                        <circle cx="44" cy="67" r="4.6" fill="none" stroke="#1a1208" strokeWidth="1.3" />
                        <circle cx="56" cy="67" r="4.6" fill="none" stroke="#1a1208" strokeWidth="1.3" />
                        <path d="M48.6 67 L51.4 67" stroke="#1a1208" strokeWidth="1.3" />
                      </svg>
                    </Silhouette>
                  </Portrait>
                </PentagonFrame>
                <CardName>Shaurya Singhal</CardName>
                <CardTitle>Duellist &amp; Spellwright</CardTitle>
                <Flourish>&#10022;</Flourish>
                <FrontStat>
                  <FrontStatValue>2244</FrontStatValue>
                  <FrontStatLabel>Guardian &middot; Top 0.8%</FrontStatLabel>
                </FrontStat>
                <FrontFoot>Tap the card to turn it over</FrontFoot>
              </Face>

              {/* ---------- back ---------- */}
              <Face $back>
                <BackTitle>Shaurya Singhal</BackTitle>
                <BackBlurb>
                  Member of Technical Staff&nbsp;2 at Adobe, Document Cloud. Formerly of
                  Naukri Campus, where his charms reached two hundred thousand souls.
                  Noted for relentless practice, an unnatural fondness for contests,
                  and a habit of running long distances to clear the mind.
                </BackBlurb>
                <Divider />
                <Grid>
                  {ACHIEVEMENTS.map((a) => (
                    <Cell key={a.label}>
                      <CellValue>{withOrdinals(a.value)}</CellValue>
                      <CellLabel>{a.label}</CellLabel>
                      <CellNote>{a.note}</CellNote>
                    </Cell>
                  ))}
                </Grid>
                <BackFoot>Chocolate Frog Card &middot; No. 2244 &middot; Rare</BackFoot>
              </Face>
            </Card>
            </CardShell>

            <CloseHint onClick={() => setOpen(false)}>Close</CloseHint>
          </Stage>
        </Backdrop>
      )}
    </>
  );
};

export default FrogCard;

/* ==================== styles ==================== */

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const leap = keyframes`
  0%   { opacity: 1; transform: translate(0, 0) scale(1) rotate(0deg); }
  35%  { opacity: 1; transform: translate(22vw, -34vh) scale(1.25) rotate(-25deg); }
  100% { opacity: 0; transform: translate(46vw, 12vh) scale(0.7) rotate(20deg); }
`;

const dealIn = keyframes`
  from { opacity: 0; transform: translateY(30px) rotateY(-24deg) scale(0.9); }
  to   { opacity: 1; transform: translateY(0) rotateY(0) scale(1); }
`;

const shimmer = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 0.75; }
`;

/* The portrait never quite holds still */
const portraitShift = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25%      { transform: translate(-2px, 1px) rotate(-1.2deg); }
  55%      { transform: translate(2px, -1px) rotate(1deg); }
  80%      { transform: translate(-1px, -1px) rotate(-0.5deg); }
`;

const Trigger = styled.button`
  position: fixed;
  right: 1.4rem;
  bottom: 7.4rem;
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: 100px;
  border: 1px solid rgba(150, 95, 40, 0.45);
  background: rgba(24, 14, 8, 0.85);
  backdrop-filter: blur(10px);
  color: #d8a86a;
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(196, 148, 42, 0.85);
    box-shadow: 0 0 22px rgba(150, 95, 40, 0.3);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) { right: 0.8rem; bottom: 6.2rem; padding: 0.4rem 0.65rem; }
`;

const TriggerGlyph = styled.span`font-size: 0.8rem; line-height: 1;`;
const TriggerText = styled.span`
  @media (max-width: 480px) { display: none; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 540;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(4, 2, 8, 0.86);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.3s ease both;
`;

const Frog = styled.div`
  position: absolute;
  left: 42%;
  top: 52%;
  font-size: 3rem;
  pointer-events: none;
  animation: ${leap} 1.5s cubic-bezier(0.3, -0.2, 0.5, 1) both;
`;

const Stage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  perspective: 1400px;
`;

/* Entrance animation lives on the shell. Keeping it on the card itself meant
   `animation-fill-mode: both` pinned the transform and the flip never applied. */
const CardShell = styled.div`
  transform-style: preserve-3d;
  animation: ${dealIn} 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both;
`;

const Card = styled.div<{ $flipped: boolean }>`
  position: relative;
  width: min(330px, 82vw);
  height: min(470px, 74vh);
  transform-style: preserve-3d;
  transition: transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
  cursor: none;
  transform: rotateY(${({ $flipped }) => ($flipped ? 180 : 0)}deg);
`;

const Face = styled.div<{ $back?: boolean }>`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 12px;
  padding: ${({ $back }) => ($back ? "1.5rem 1.3rem 1.1rem" : "1.5rem 1.2rem")};
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  /* Foil-stamped chocolate-box card */
  background:
    repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 4px),
    linear-gradient(155deg, #6d2530 0%, #4a1620 45%, #5c1d28 75%, #3d1119 100%);
  border: 2px solid #c8a24a;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.75), inset 0 0 40px rgba(0, 0, 0, 0.45);
  ${({ $back }) => $back && css`transform: rotateY(180deg);`}
`;

const Filigree = styled.div`
  position: absolute;
  inset: 8px;
  border: 1px solid rgba(200, 162, 74, 0.35);
  border-radius: 8px;
  pointer-events: none;

  &::before, &::after {
    content: "\\2740";
    position: absolute;
    color: rgba(200, 162, 74, 0.6);
    font-size: 0.7rem;
  }
  &::before { top: -7px; left: 50%; transform: translateX(-50%); background: #4a1620; padding: 0 6px; }
  &::after  { bottom: -7px; left: 50%; transform: translateX(-50%); background: #3d1119; padding: 0 6px; }
`;

const PentagonFrame = styled.div`
  position: relative;
  width: 178px;
  height: 200px;
  margin-top: 0.6rem;
  background: linear-gradient(160deg, #d9b45a, #a37c2c);
  clip-path: polygon(50% 0%, 100% 32%, 82% 100%, 18% 100%, 0% 32%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Portrait = styled.div`
  position: relative;
  width: 164px;
  height: 186px;
  background: radial-gradient(circle at 50% 35%, #2c2136, #150e1c);
  clip-path: polygon(50% 0%, 100% 32%, 82% 100%, 18% 100%, 0% 32%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
`;

const PortraitGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 30%, rgba(255, 205, 120, 0.22), transparent 65%);
  animation: ${shimmer} 3.4s ease-in-out infinite;
  pointer-events: none;
`;

const Silhouette = styled.div`
  width: 100%;
  height: 92%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${portraitShift} 7s ease-in-out infinite;

  svg { width: 78%; height: 100%; }
`;

const CardName = styled.h3`
  margin-top: 0.9rem;
  font-family: "Cinzel", serif;
  font-size: 1.12rem;
  font-weight: 700;
  color: #f4e3bd;
  text-align: center;
  text-shadow: 0 0 16px rgba(200, 162, 74, 0.45);
`;

const CardTitle = styled.p`
  font-family: "Crimson Text", serif;
  font-style: italic;
  font-size: 0.8rem;
  color: #d0a86a;
  margin-top: 0.15rem;
`;

const Flourish = styled.div`
  color: rgba(200, 162, 74, 0.7);
  font-size: 0.7rem;
  margin: 0.7rem 0 0.5rem;
`;

const FrontStat = styled.div`
  text-align: center;
  padding: 0.5rem 1.1rem;
  border: 1px solid rgba(200, 162, 74, 0.3);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.22);
`;

const FrontStatValue = styled.div`
  font-family: "Cinzel", serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffd97a;
  line-height: 1;
`;

const FrontStatLabel = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.45rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(216, 192, 154, 0.8);
  margin-top: 0.25rem;
`;

const FrontFoot = styled.p`
  margin-top: auto;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.46rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(226, 199, 154, 0.55);
`;

const BackTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: #f4e3bd;
  text-align: center;
`;

const BackBlurb = styled.p`
  margin-top: 0.6rem;
  font-family: "Crimson Text", serif;
  font-size: 0.74rem;
  line-height: 1.6;
  color: #dcc39c;
  text-align: center;
`;

const Divider = styled.div`
  width: 60%;
  height: 1px;
  margin: 0.8rem 0;
  background: linear-gradient(90deg, transparent, rgba(200, 162, 74, 0.6), transparent);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem 0.7rem;
  width: 100%;
`;

const Cell = styled.div`
  text-align: center;
  padding: 0.3rem 0.15rem;
  border: 1px solid rgba(200, 162, 74, 0.18);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
`;

const CellValue = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: #ffd97a;
`;

const Ord = styled.sup`
  font-family: "Raleway", sans-serif;
  font-size: 0.5em;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0;
  vertical-align: super;
  margin-left: 0.06em;
`;

const CellLabel = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.5rem;
  color: #d8c09a;
  margin-top: 0.05rem;
`;

const CellNote = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  color: rgba(200, 170, 130, 0.65);
  margin-top: 0.05rem;
`;

const BackFoot = styled.p`
  margin-top: auto;
  padding-top: 0.6rem;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.45rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(226, 199, 154, 0.5);
`;

const CloseHint = styled.button`
  font-family: "Cinzel", serif;
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #9a8a80;
  background: none;
  border: none;
  cursor: none;
  transition: color 0.25s ease;

  &:hover { color: #e2cf9a; }
`;
